// Reservation API service for backend integration
import type {
  ReserveTable,
  PaginatedResponse,
  RejectRequestDto,
  ReserveTableDTO,
  AcceptRessponseDto,
  BookingRequestDto,
  BookingResponseDto
} from '../types/reservations';
import type { MergeGroupResponseDto } from '../types/tableMerge';
import { BookingStatus } from '../types/reservations';
import { API_CONFIG, API_ENDPOINTS } from '../constants/config';
import { notificationApi } from './notificationApi';


class ReservationApiService {

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend CORS configuration)
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? '' : (API_CONFIG.BASE_URL || 'https://admin.grandpittu.lk');
    const url = `${baseUrl}${API_CONFIG.SLUG}${endpoint}`;

    // Headers for the request - no need for Authorization header, cookies are sent automatically
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

      const response = await fetch(url, {
        ...options,
        mode: 'cors',
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check if response is actually JSON
      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        // Handle non-JSON responses (likely HTML error pages)
        const text = await response.text();
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}...`);
      }

      if (!response.ok) {

        let serverError;

        // Try to parse as JSON first for all status codes, including 500
        try {
          const errorData = await response.json();
          serverError = { status: response.status, ...errorData };
        } catch {
          // If JSON parsing fails, try text
          try {
            const errorText = await response.text();
            serverError = { status: response.status, message: errorText };
          } catch {
            serverError = { status: response.status, message: `API Error: ${response.status} - ${response.statusText}` };
          }
        }

        // Throw the server error object directly so the error handler can access all data
        const error = new Error(JSON.stringify(serverError));
        error.name = 'ServerError';
        throw error;
      }

      return await response.json();
    } catch (error: unknown) {

      // Determine if this is a mutation (non-idempotent) request
      const method = (options.method || 'GET').toUpperCase();
      const isMutationRequest = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);

      const isRetryable = error instanceof Error && (
        error.name === 'TimeoutError' ||
        // Only retry AbortError for GET requests (safe to retry)
        (error.name === 'AbortError' && !isMutationRequest) ||
        error.message?.includes('fetch') ||
        error.message?.includes('network') ||
        // Don't retry 500 errors for mutations - might indicate successful processing
        (error.message?.includes('500') && !isMutationRequest) ||
        error.message?.includes('session error')
      );

      if (isRetryable && retryCount < API_CONFIG.RETRY_ATTEMPTS) {
        const delay = API_CONFIG.EXPONENTIAL_BACKOFF
          ? API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount)
          : API_CONFIG.RETRY_DELAY;

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequest<T>(endpoint, options, retryCount + 1);
      }

      throw error;
    }

  }

  async getAcceptedBookings(page = 0, size = 50, floorId?: number, type: string = 'all', date?: string): Promise<PaginatedResponse<ReserveTable>> {

    let endpoint = `${API_ENDPOINTS.ADMIN.GET_ACCEPTED_BOOKINGS}?page=${page}&size=${size}`;

    // Add floorId and type parameters
    if (floorId !== undefined) {
      endpoint += `&floorId=${floorId}`;
    }
    endpoint += `&type=${type}`;

    // Add date parameter for date-specific filtering
    if (date) {
      endpoint += `&date=${date}`;
    }

    const response = await this.makeRequest<PaginatedResponse<ReserveTable>>(endpoint);

    return response;
  }


  async getPendingBookings(page = 0, size = 50): Promise<PaginatedResponse<ReserveTable>> {

    const endpoint = `${API_ENDPOINTS.ADMIN.GET_PENDING_BOOKINGS}?page=${page}&size=${size}`;
    return this.makeRequest<PaginatedResponse<ReserveTable>>(endpoint);
  }

  // Get bookings by other status (Rejected, Completed, etc.) - Updated to use zero-indexed pagination
  async getBookingsByStatus(
    status: string,
    page = 0,
    size = 50
  ): Promise<PaginatedResponse<ReserveTable>> {

    const endpoint = `${API_ENDPOINTS.ADMIN.GET_OTHER_STATUS_BOOKINGS}?page=${page}&size=${size}&status=${status}`;
    const response = await this.makeRequest<PaginatedResponse<ReserveTable>>(endpoint);


    return response;
  }

  // Manage booking (accept/reject/complete/cancel)
  async manageBooking(request: RejectRequestDto): Promise<AcceptRessponseDto> {

    try {
      const response = await this.makeRequest<AcceptRessponseDto>(API_ENDPOINTS.ADMIN.MANAGE_BOOKING, {
        method: 'POST',
        body: JSON.stringify(request),
      });

      return response;
    } catch (error) {
      throw error;
    }
  }


  async searchReservations(): Promise<ReserveTableDTO[]> {
    return this.makeRequest<ReserveTableDTO[]>(API_ENDPOINTS.RESERVE_TABLE.SEARCH);
  }

  async searchReservationsByPeriod(): Promise<ReserveTableDTO[]> {
    return this.makeRequest<ReserveTableDTO[]>(API_ENDPOINTS.RESERVE_TABLE.SEARCH_BY_PERIOD);
  }

  async searchReservationsWithValue(searchValue: string): Promise<ReserveTableDTO[]> {
    const endpoint = `${API_ENDPOINTS.RESERVE_TABLE.SEARCH}?searchValue=${encodeURIComponent(searchValue)}`;
    return this.makeRequest<ReserveTableDTO[]>(endpoint);
  }

  async searchReservationsByPeriodWithParams(date: string, period: string): Promise<ReserveTableDTO[]> {
    const endpoint = `${API_ENDPOINTS.RESERVE_TABLE.SEARCH_BY_PERIOD}?date=${date}&period=${period}`;
    return this.makeRequest<ReserveTableDTO[]>(endpoint);
  }

  // Get reservation for a specific table on a specific date
  async getReservationByTableAndDate(tableId: number, date?: string): Promise<ReserveTableDTO | null> {
    try {
      const queryDate = date || new Date().toISOString().split('T')[0];
      const allReservations = await this.searchReservationsByPeriodWithParams(queryDate, 'today');

      // Find reservation that includes this table
      const reservation = allReservations.find(r =>
        r.restaurantTableId.some(t => t.id === tableId)
      );

      return reservation || null;
    } catch (error) {
      return null;
    }
  }

  async checkTableAvailability(): Promise<boolean> {
    return this.makeRequest<boolean>(API_ENDPOINTS.RESERVE_TABLE.CHECK_AVAILABILITY);
  }

  async checkTableAvailabilityWithParams(
    date: string,
    time: string,
    tableNo: number
  ): Promise<boolean> {
    const endpoint = `${API_ENDPOINTS.RESERVE_TABLE.CHECK_AVAILABILITY}?date=${date}&time=${time}&tableNo=${tableNo}`;
    return this.makeRequest<boolean>(endpoint);
  }

  // Create new booking (public endpoint)
  async createBooking(request: BookingRequestDto): Promise<BookingResponseDto> {
    return this.makeRequest<BookingResponseDto>(API_ENDPOINTS.BOOKING.CREATE, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  // Convenience methods for common operations with notifications
  async acceptBooking(
    bookingId: number,
    restaurantTableId: number,
    message?: string,
    reservationDetails?: { customerName: string; customerEmail: string; date: string; time: string; tableNames: string[]; guestCount: number }
  ): Promise<AcceptRessponseDto> {
    const response = await this.manageBooking({
      bookingId,
      message,
      bookingStatus: BookingStatus.ACCEPTED,
      restaurantTableId: [restaurantTableId]
    });

    // Send confirmation notification if customer details are provided
    if (reservationDetails) {
      try {
        await notificationApi.sendBookingConfirmation(
          reservationDetails.customerEmail,
          reservationDetails.customerName,
          {
            bookingId: bookingId.toString(),
            date: reservationDetails.date,
            time: reservationDetails.time,
            tableNames: reservationDetails.tableNames,
            guestCount: reservationDetails.guestCount
          }
        );
      } catch (error) {

      }
    }

    return response;
  }

  async rejectBooking(
    bookingId: number,
    restaurantTableId: number,
    message?: string,
    customerDetails?: { customerName: string; customerEmail: string }
  ): Promise<AcceptRessponseDto> {
    const response = await this.manageBooking({
      bookingId,
      message,
      bookingStatus: BookingStatus.REJECTED,
      restaurantTableId: [restaurantTableId] // Backend expects array
    });

    // Send rejection notification if customer details are provided
    if (customerDetails) {
      try {
        const rejectionMessage = message || 'Unfortunately, we cannot accommodate your reservation request at this time.';
        await notificationApi.sendBookingUpdate(
          customerDetails.customerEmail,
          customerDetails.customerName,
          bookingId.toString(),
          `Your reservation request has been declined. ${rejectionMessage}`,
          'status_change'
        );
      } catch (error) {
      }
    }

    return response;
  }

  async completeBooking(bookingId: number, restaurantTableId: number, message?: string): Promise<AcceptRessponseDto> {
    return this.manageBooking({
      bookingId,
      message,
      bookingStatus: BookingStatus.COMPLETED,
      restaurantTableId: [restaurantTableId]
    });
  }

  async cancelBooking(
    bookingId: number,
    restaurantTableIds: number[],
    message?: string,
    customerDetails?: { customerName: string; customerEmail: string },
    mergeGroupName?: string,
    mergeGroupColor?: string
  ): Promise<AcceptRessponseDto> {

    const request = {
      bookingId,
      message: message || `Reservation cancelled by admin`,
      bookingStatus: BookingStatus.CANCELED,
      restaurantTableId: restaurantTableIds && restaurantTableIds.length > 0 ? restaurantTableIds : [],
      // Include merge group metadata when canceling merged table reservations (2+ tables)
      ...(restaurantTableIds.length > 1 && mergeGroupName && mergeGroupColor && {
        mergeGroupName,
        mergeGroupColor
      })
    };

    const response = await this.manageBooking(request);

    // Send cancellation notification if customer details are provided
    if (customerDetails) {
      try {
        await notificationApi.sendBookingCancellation(
          customerDetails.customerEmail,
          customerDetails.customerName,
          bookingId.toString(),
          message
        );
      } catch (error) {
      }
    }

    return response;
  }

  // Get all bookings regardless of status (for admin overview) - Updated to use zero-indexed pagination
  async getAllBookings(page = 0, size = 50): Promise<{
    accepted: PaginatedResponse<ReserveTable>;
    pending: PaginatedResponse<ReserveTable>;
    rejected: PaginatedResponse<ReserveTable>;
    completed: PaginatedResponse<ReserveTable>;
  }> {
    const [accepted, pending, rejected, completed] = await Promise.all([
      this.getAcceptedBookings(page, size),
      this.getPendingBookings(page, size),
      this.getBookingsByStatus('Rejected', page, size),
      this.getBookingsByStatus('Completed', page, size),
    ]);

    return { accepted, pending, rejected, completed };
  }

  // Standardized method to get consistent reservation counts across components
  async getStandardizedReservations(): Promise<{
    current: ReserveTable[];  // Only pending, accepted, cancelled for current view
    all: ReserveTable[];      // All reservations for history
    counts: {
      total: number;
      pending: number;
      accepted: number;
      cancelled: number;
      completed: number;
      rejected: number;
    };
  }> {
    try {

      const [accepted, pending, rejected, completed, canceled, incompleted] = await Promise.allSettled([
        this.getBookingsByStatus('Accepted', 0, 50).then(result => {
          return result;
        }).catch(err => {
          return { content: [], totalElements: 0 };
        }),

        this.getPendingBookings(0, 50).then(result => {
          return result;
        }).catch(err => {
          return { content: [], totalElements: 0 };
        }),
        this.getBookingsByStatus('Rejected', 0, 50).then(result => {
          return result;
        }).catch(err => {
          return { content: [], totalElements: 0 };
        }),
        this.getBookingsByStatus('Completed', 0, 50).then(result => {
          return result;
        }).catch(err => {
          return { content: [], totalElements: 0 };
        }),
        this.getBookingsByStatus('Canceled', 0, 50).then(result => {
          return result;
        }).catch(err => {
          return { content: [], totalElements: 0 };
        }),
        this.getBookingsByStatus('Incompleted', 0, 50).then(result => {
          return result;
        }).catch(err => {
          return { content: [], totalElements: 0 };
        }),
      ]);

      // Extract successful results
      const acceptedData = accepted.status === 'fulfilled' ? accepted.value : { content: [], totalElements: 0 };
      const pendingData = pending.status === 'fulfilled' ? pending.value : { content: [], totalElements: 0 };
      const rejectedData = rejected.status === 'fulfilled' ? rejected.value : { content: [], totalElements: 0 };
      const completedData = completed.status === 'fulfilled' ? completed.value : { content: [], totalElements: 0 };
      const canceledData = canceled.status === 'fulfilled' ? canceled.value : { content: [], totalElements: 0 };
      const incompletedData = incompleted.status === 'fulfilled' ? incompleted.value : { content: [], totalElements: 0 };

      // Combine all reservations
      const allReservations = [
        ...acceptedData.content,
        ...pendingData.content,
        ...rejectedData.content,
        ...completedData.content,
        ...canceledData.content,
        ...incompletedData.content,
      ];

      // For current reservations - only show pending, accepted (accepted), and cancelled
      const currentReservations = [
        ...pendingData.content,
        ...acceptedData.content,
        ...canceledData.content,
      ];

      // Calculate counts
      const counts = {
        total: allReservations.length,
        pending: pendingData.content.length,
        accepted: acceptedData.content.length,
        cancelled: canceledData.content.length,
        completed: completedData.content.length,
        rejected: rejectedData.content.length,
      };


      const result = {
        current: currentReservations,
        all: allReservations,
        counts
      };

      return result;
    } catch (error) {
      // Return empty data structure on error
      return {
        current: [],
        all: [],
        counts: {
          total: 0,
          pending: 0,
          accepted: 0,
          cancelled: 0,
          completed: 0,
          rejected: 0,
        }
      };
    }
  }

  // Get all reservations for history (combining all statuses) - Updated to use zero-indexed pagination
  async getAllReservationsForHistory(page = 0, size = 50): Promise<{
    all: ReserveTable[];
    totalCount: number;
    totalPages: number;
  }> {
    const standardizedData = await this.getStandardizedReservations();

    // Sort by date and time (newest first)
    const sortedReservations = standardizedData.all.sort((a, b) => {
      const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
      if (dateCompare === 0) {
        // If dates are same, sort by time
        return b.time.localeCompare(a.time);
      }
      return dateCompare;
    });

    // Calculate total count from standardized data
    const totalCount = standardizedData.counts.total;

    // Apply pagination to the sorted results - Use zero-indexed pagination
    const startIndex = page * size;
    const paginatedResults = sortedReservations.slice(startIndex, startIndex + size);

    return {
      all: paginatedResults,
      totalCount,
      totalPages: Math.ceil(totalCount / size),
    };
  }

  // Merge Group Management Methods


  async getMergeGroups(date: string): Promise<MergeGroupResponseDto[]> {
    const endpoint = `${API_ENDPOINTS.RESERVE_TABLE.GET_ALL_MERGE_GROUP}?date=${date}`;
    return this.makeRequest<MergeGroupResponseDto[]>(endpoint);
  }



  async deleteMergeGroup(id: number, status: string = 'CANCELED'): Promise<string> {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend CORS configuration)
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? '' : (API_CONFIG.BASE_URL || 'https://admin.grandpittu.lk');
    const endpoint = `${baseUrl}${API_CONFIG.SLUG}${API_ENDPOINTS.RESERVE_TABLE.DELETE_MERGE_GROUP(id)}?status=${status}`;
    return this.makeTextRequest(endpoint, {
      method: 'DELETE',
    });
  }



  private async makeTextRequest(url: string, options: RequestInit = {}): Promise<string> {
    // Headers for the request - no need for Authorization header, cookies are sent automatically
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'text/plain', // Expecting text response instead of JSON
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

      const response = await fetch(url, {
        ...options,
        mode: 'cors',
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        let errorMessage = `Request failed with status: ${response.status}`;

        try {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        } catch {
          // Keep original message
        }

        throw new Error(errorMessage);
      }

      return await response.text();
    } catch (error: unknown) {
      throw error;
    }
  }
}

// Export singleton instance
export const reservationApi = new ReservationApiService();
export default reservationApi;
